import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { useCheckAuthorizeBeforeAction, useResponsive } from 'hooks'
import { logEvent } from 'lib/analytics'
import { useCurrentUser } from 'store/api/yay/user'
import {
  useFindChatRoomMutationMutation,
  useCreateChatRoomMutation,
  useDeleteChatRoomMutation,
  useHideChatRoomMutation,
  usePinChatRoomMutation,
  useUnPinChatRoomMutation,
  useCreateSingleUserChatMutation,
} from 'store/api/yay/chat'
import { setSelectedChatRoomId } from 'store/slices/chat'

import { useNavigate } from 'features/i18n/router'
import useGlobalConfirmBox from 'features/GlobalConfirmBox/hooks/useGlobalConfirmBox'
import usePageModal from 'features/Modal/hooks/usePageModal'

import { MODAL_MODE } from 'components/Modal/config'
import { ROUTE_PATH } from 'lib/paths'
import { useCallback } from 'react'

const useChatRoomLogic = ({ chatRoomId }: { chatRoomId?: number } = {}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { open } = usePageModal()

  const { isMobile } = useResponsive()

  const checkAuthorizeBeforeAction = useCheckAuthorizeBeforeAction()
  const { showConfirmBox } = useGlobalConfirmBox()

  const [hideChatRoom] = useHideChatRoomMutation()
  const [pinChatRoom] = usePinChatRoomMutation()
  const [unPinChatRoom] = useUnPinChatRoomMutation()
  const [findChatRoom, findChatRoomRes] = useFindChatRoomMutationMutation()
  const [createChatRoom, createChatRoomRes] = useCreateChatRoomMutation()
  const [createSingleChatRoom, createSingleChatRoomRes] =
    useCreateSingleUserChatMutation()
  const [deleteChatRoom, deleteChatRoomRes] = useDeleteChatRoomMutation()

  const { data: currentUser } = useCurrentUser()

  const redirectToChat = useCallback(
    (chatRoomId: string, goBack: boolean) => {
      if (isMobile) {
        open(MODAL_MODE.CHAT_ROOM, {
          chatRoomId,
          ...(goBack && { goBack }),
        })
      } else {
        dispatch(setSelectedChatRoomId(Number(chatRoomId)))
        navigate(ROUTE_PATH.CHAT)
      }
    },
    [isMobile]
  )

  const onCreateChatRoom = checkAuthorizeBeforeAction(
    async ({
      userIds,
      goBack,
      isHimaChat,
      from,
    }: {
      userIds: Array<number>
      goBack?: boolean
      isHimaChat?: boolean
      from?: any
    }) => {
      if (userIds?.length == 0 && userIds[0] == currentUser?.id) {
        return
      }

      if (userIds?.length == 0) {
        const chatRoomRes = await findChatRoom({ userId: userIds[0] })

        if (chatRoomRes?.['data']?.result != 'success') {
          return
        }

        const chatRoomId = chatRoomRes?.['data']?.chat_room_id

        if (chatRoomId) {
          logEvent.chat_room_open({
            from,
            target_chat_room_id: chatRoomId,
          })

          return redirectToChat(chatRoomId, goBack)
        }
      }

      let resp

      if (userIds?.length == 1) {
        resp = await createSingleChatRoom({
          withUserId: userIds[0],
          himaChat: isHimaChat,
        })
      } else {
        resp = await createChatRoom({
          withUserIds: userIds,
          himaChat: isHimaChat,
        })
      }

      if (resp?.['data']?.result == 'success') {
        logEvent.chat_room_create()
        logEvent.chat_room_open({
          from,
          target_chat_room_id: resp?.['data']?.room_id,
        })

        redirectToChat(resp?.['data']?.room_id, false)
      } else {
        switch (resp?.['error']?.data?.error_code) {
          case -338: {
            showConfirmBox({
              title: t('confirm_box_create_chat.title'),
              content: (
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('confirm_box_create_chat.content.diff_age'),
                  }}
                />
              ),
              confirmBoxProps: {
                cancelContent: <></>,
              },
            })

            break
          }
          case -313: {
            showConfirmBox({
              title: t('confirm_box_create_chat.title'),
              content: (
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('confirm_box_create_chat.content.chat_mutual'),
                  }}
                />
              ),
              confirmBoxProps: {
                cancelContent: <></>,
              },
            })

            break
          }
        }
      }
    }
  )

  const onDeleteChat = ({
    redirectUrl,
    onSuccess,
    onError,
    isChatOwner,
  }: {
    redirectUrl?: string
    onSuccess?: Function
    onError?: Function
    isChatOwner?: boolean
  } = {}) => {
    showConfirmBox({
      title: isChatOwner
        ? t('delete_chat_room_confirm_box.title')
        : t('chat_room_detail_modal.body_section.setting.leave_title'),
      content: (
        <span
          dangerouslySetInnerHTML={{
            __html: isChatOwner
              ? t('delete_chat_room_confirm_box.content')
              : t('chat_room_detail_modal.body_section.setting.leave_body'),
          }}
        />
      ),
      confirmBoxProps: {
        onOk: async () => {
          const res = await deleteChatRoom({
            chatRoomIds: [chatRoomId],
          })

          if (res?.['data']?.result == 'success') {
            if (onSuccess) {
              onSuccess?.()
              return
            }

            navigate(redirectUrl ?? ROUTE_PATH.CHAT)
          } else {
            if (onError) onError()
          }
        },
        confirmButtonText: isChatOwner
          ? t('common.delete')
          : t('chat_room_detail_modal.body_section.setting.leave'),
      },
    })
  }

  const onPinChatRoom = async () => {
    const res = await pinChatRoom(chatRoomId)

    if (typeof res['error'] === 'undefined') {
      window.location.reload()
    }
  }

  const onUnPinChatRoom = async () => {
    const res = await unPinChatRoom(chatRoomId)

    if (typeof res['error'] === 'undefined') {
      window.location.reload()
    }
  }

  const onHideChatRoom = () => {
    showConfirmBox({
      title: t('chatroom.action.hide.modal.title'),
      content: t('chatroom.action.hide.modal.content'),
      confirmBoxProps: {
        confirmButtonText: t('common.hide'),
        onOk: async () => {
          const res = await hideChatRoom(chatRoomId)

          if (typeof res['error'] === 'undefined') {
            window.location.reload()
          }
        },
      },
    })
  }

  return {
    onCreateChatRoom,
    onCreateChatRoomIsLoading:
      createChatRoomRes.isLoading || findChatRoomRes.isLoading,
    onDeleteChat,
    onDeleteChatIsLoading: deleteChatRoomRes.isLoading,
    onPinChatRoom,
    onUnPinChatRoom,
    onHideChatRoom,
  }
}

export default useChatRoomLogic
